import type {
  ElementType,
  FunctionComponent,
  HTMLProps,
  MouseEventHandler,
  ReactNode,
} from 'react'
import React from 'react'

import classnames from 'classnames'

import type { LinkAppearance } from '../../types'
import { isExternalLink } from '../../utilities/isExternalLink/IsExternaLink'
import { AnimationWrapper } from '../AnimationWrapper'
import { ChevronRightIcon, ExternalLinkIcon } from '../Icons/Navigational'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './Link.module.scss'
import { validateHrefHostname } from './Link.utils'

// #region Types
export type LinkProps = {
  href?: string | null
  appearance?: LinkAppearance
  textStyle?: LinkStyleText
  badge?: ElementType
  icon?: ElementType
  includeIcon?: boolean
  includeAnimation?: boolean
  includeExternalLinkIcon?: boolean
  includeNoReferrer?: boolean
  checkIsExternal?: boolean
  className?: string
  children?: ReactNode
  clickHandler?: MouseEventHandler<HTMLAnchorElement>
  [key: `data-${string}`]: string | undefined
} & HTMLProps<HTMLAnchorElement>

export type LinkStyleText =
  | 'sb-text-body-default-regular'
  | 'sb-text-interface-body-default-regular'
  | 'sb-text-body-default-strong'
  | 'sb-text-interface-body-small-regular'
  | 'sb-text-body-small-regular'
  | 'sb-text-interface-body-x-small-regular'
  | 'sb-text-interface-body-small-strong'
  | 'sb-text-heading-small'
  | 'sb-text-heading-medium'
// #endregion

export const Link: FunctionComponent<LinkProps> = ({
  children,
  href,
  appearance = 'primary',
  textStyle = 'sb-text-body-default-regular',
  badge: Badge,
  icon: Icon,
  includeIcon = false,
  includeAnimation = true,
  includeExternalLinkIcon = false,
  includeNoReferrer = false,
  checkIsExternal = true,
  className,
  clickHandler,
  ...rest
}) => {
  const isValidHref = validateHrefHostname(href)

  // The logic around this is firstly based on a prop to set an external link icon manually on rhs of text
  // If no prop is passed and default value is used we do make a extra check against the href
  // This is mainly to handle article body links where we have no control as they get mapped from glide RTE content
  const renderExternalLinkIcon =
    href && checkIsExternal ? isExternalLink(href) : includeExternalLinkIcon
  const commonProps = {
    className: classnames(
      styles.link,
      {
        [styles.linkPrimary]: appearance === 'primary',
        [styles.linkSecondary]: appearance === 'secondary',
      },
      className
    ),
    ...(renderExternalLinkIcon && {
      target: '_blank',
      rel: 'nofollow noopener noreferrer',
    }),
    ...(includeNoReferrer && { rel: 'noreferrer' }),
    ...(clickHandler && { onClick: clickHandler }),
    ...rest,
  }
  const linkChildrenProps: LinkChildrenProps = {
    children,
    appearance,
    textStyle,
    badge: Badge,
    icon: Icon,
    includeIcon,
    includeAnimation,
    renderExternalLinkIcon,
  }

  return isValidHref ? (
    <a href={href} {...commonProps}>
      <LinkChildren {...linkChildrenProps} />
    </a>
  ) : (
    <LinkChildren
      includeAnimation={false}
      renderExternalLinkIcon={false}
      textStyle={textStyle}
      children={children}
    />
  )
}

type LinkChildrenProps = LinkProps & { renderExternalLinkIcon?: boolean }

export const LinkChildren: FunctionComponent<LinkChildrenProps> = ({
  children,
  appearance = 'primary',
  textStyle = 'sb-text-body-default-regular',
  badge: Badge,
  icon: Icon,
  includeIcon = false,
  includeAnimation = true,
  renderExternalLinkIcon,
}) => {
  const IconComponent = Icon || ChevronRightIcon

  return (
    <>
      {Badge && <Badge />}
      {(Icon || includeIcon) && <IconComponent className={styles.linkIcon} />}
      <TypographyV2 tag="span" textStyle={textStyle}>
        {includeAnimation ? (
          <AnimationWrapper inverted={appearance === 'secondary'}>
            {children}
          </AnimationWrapper>
        ) : (
          children
        )}
      </TypographyV2>
      {renderExternalLinkIcon && (
        <ExternalLinkIcon className={styles.externalIcon} />
      )}
    </>
  )
}
